import React, { useMemo } from 'react';
import { createColumnHelper, ColumnDef } from '@tanstack/react-table';
import { Switch } from '@blueprintjs/core';
import { FieldMappingTreeNode } from '../../../../../../utils/fieldMappingTreeBuilder';
import { ValidationResult } from '../../../../../../utils/fieldMappingValidator';
import { MappingMode } from '../../../../../../types/DataSyncItem';
import { HeaderInfo } from '../types';
import { SourceFieldCell } from '../components/SourceFieldCell';
import { DestinationFieldCell } from '../components/DestinationFieldCell';
import { ObjectIcon, IconSize } from '../../../ObjectIcon';
import styles from '../components/TreeFieldMappingsTable.module.sass';

const columnHelper = createColumnHelper<FieldMappingTreeNode>();

export interface UseTableColumnsParams {
  fieldMappingMode: MappingMode;
  sourceFieldDisplayMap: Map<string, string>;
  sourceFieldValidations: Map<string, ValidationResult>;
  showRequiredHint: boolean;
  sourceInfo?: HeaderInfo;
  destinationInfo?: HeaderInfo;
  handlers: {
    onOpenDialog: (path: string) => void;
    onRemoveMapping: (path: string) => void;
    onToggleDisabled: (path: string) => void;
  };
}

/**
 * Custom hook for generating table column definitions
 * Separates column logic from main component for better maintainability
 */
export const useTableColumns = ({
  fieldMappingMode,
  sourceFieldDisplayMap,
  sourceFieldValidations,
  showRequiredHint,
  sourceInfo,
  destinationInfo,
  handlers,
}: UseTableColumnsParams): Array<ColumnDef<FieldMappingTreeNode, any>> => {
  return useMemo(
    () => {
      const baseColumns: Array<ColumnDef<FieldMappingTreeNode, any>> = [
      // Source Field Column
      columnHelper.accessor('sourceField', {
        header: () => (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {sourceInfo?.iconUrl && (
              <ObjectIcon
                src={sourceInfo.iconUrl}
                alt={sourceInfo.name}
                size={IconSize.SMALL}
              />
            )}
            <span>{sourceInfo?.name || 'Source Field'}</span>
          </div>
        ),
        size: 300,
        cell: ({ row }) => (
          <SourceFieldCell
            node={row.original}
            fieldMappingMode={fieldMappingMode}
            sourceFieldDisplayMap={sourceFieldDisplayMap}
            sourceFieldValidations={sourceFieldValidations}
            showRequiredHint={showRequiredHint}
            onOpenDialog={handlers.onOpenDialog}
            onRemoveMapping={handlers.onRemoveMapping}
          />
        ),
      }),

      // Destination Field Column
      columnHelper.accessor('displayName', {
        header: () => (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {destinationInfo?.iconUrl && (
              <ObjectIcon
                src={destinationInfo.iconUrl}
                alt={destinationInfo.name}
                size={IconSize.SMALL}
              />
            )}
            <span>{destinationInfo?.name || 'Destination Field'}</span>
          </div>
        ),
        size: 300,
        cell: (info) => (
          <DestinationFieldCell
            row={info.row}
            node={info.row.original}
          />
        ),
      }),
      ];

      // Add actions column for FIXED mode
      if (fieldMappingMode === MappingMode.FIXED) {
        baseColumns.push(
          columnHelper.display({
            id: 'actions',
            header: '',
            size: 100,
            cell: ({ row }) => {
              const node = row.original;

              // Only show for leaf nodes with a mapped source field
              if (!node.isLeaf || !node.sourceField) {
                return null;
              }

              return (
                <div className={styles.switchCell}>
                  {!node.required && (
                    // Show enable/disable switch for non-required fields
                    <Switch
                      checked={!node.disabled}
                      onChange={() => handlers.onToggleDisabled(node.path)}
                      alignIndicator="right"
                      className={styles.enableSwitch}
                    />
                  )}
                </div>
              );
            },
          })
        );
      }

      return baseColumns;
    },
    [
      fieldMappingMode,
      sourceFieldDisplayMap,
      sourceFieldValidations,
      showRequiredHint,
      sourceInfo,
      destinationInfo,
      handlers,
    ]
  );
};
